import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { getPost } from '../api/posts';
import { getUserPostsPath } from '../config/env';

/**
 * 言語切り替えボタンコンポーネント
 * 現在のページの言語を切り替える
 */
const LanguageSwitcher: React.FC = () => {
  const lang = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const postsPath = getUserPostsPath();

  const handleLanguageSwitch = async (targetLang: 'ja' | 'en') => {
    // 既にその言語のページにいる場合は何もしない
    if (lang === targetLang) {
      return;
    }

    // 記事詳細ページの判定
    const escapedPostsPath = postsPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const postDetailPattern = new RegExp(`^/${lang}${escapedPostsPath}/([^/?#]+)$`);
    const match = location.pathname.match(postDetailPattern);

    if (match && match[1]) {
      // 記事詳細ページの場合
      const slug = decodeURIComponent(match[1]);
      try {
        // 同じslugで別言語版の記事を取得
        await getPost(slug, targetLang);
        // 取得できた場合は、同じslugで別言語版のURLを生成
        navigate(`/${targetLang}${postsPath}/${slug}${location.search}${location.hash}`);
        return;
      } catch (err) {
        // 記事が見つからない（別言語版がない）場合は、ホームページに遷移
        navigate(`/${targetLang}${location.search}${location.hash}`);
        return;
      }
    }

    // その他のページの場合
    const currentPath = location.pathname;
    let pathWithoutLang = currentPath.replace(/^\/[^/]+/, '') || '/';
    if (pathWithoutLang === '/') {
      pathWithoutLang = '';
    }
    const targetPath = `/${targetLang}${pathWithoutLang}${location.search}${location.hash}`;
    navigate(targetPath);
  };

  const handleJapaneseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleLanguageSwitch('ja');
  };

  const handleEnglishClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleLanguageSwitch('en');
  };

  return (
    <div className="language-switcher">
      <button
        onClick={handleJapaneseClick}
        className={`language-switcher__item ${lang === 'ja' ? 'language-switcher__item--active' : ''}`}
        aria-label="日本語に切り替え"
        disabled={lang === 'ja'}
      >
        日本語
      </button>
      <span className="language-switcher__separator">|</span>
      <button
        onClick={handleEnglishClick}
        className={`language-switcher__item ${lang === 'en' ? 'language-switcher__item--active' : ''}`}
        aria-label="Switch to English"
        disabled={lang === 'en'}
      >
        English
      </button>
    </div>
  );
};

export default LanguageSwitcher;
